import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { Button } from '@components/main-components/button/Button';
import type { Pathname } from '@components/main-components/link/AccessibleLink';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { MediaQuery } from '@context/mediaQuery';
import type { ParamsValues } from '@context/params';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { SeeAllUserInfoFragmentDoc } from '@graphql/reactQuery';
import { DataTestId } from '@utils/test';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment SeeAllUserInfo on User {
    id
    webappUri
  }
`);

export const SeeAllLink = ({
  user: userFragment,
  path,
  params,
  className,
}: {
  user: FragmentType<SeeAllUserInfoFragmentDoc>;
  path: Pathname;
  params: ParamsValues | null;
  className?: string;
}) => {
  const { tablet } = MediaQuery.useContainer();

  const user = getFragment(SeeAllUserInfoFragmentDoc, userFragment);

  const href = webappUriPath(user.webappUri, {
    subpath: path,
    params,
  });

  return (
    <AccessibleLinkSeeAll pathnameUri={href} isTablet={tablet} className={className}>
      <SeeAllContainer data-testid={DataTestId.SEE_ALL_LINK(href)}>
        <Button label="See All" variant="tertiary" size="XS" />
      </SeeAllContainer>
    </AccessibleLinkSeeAll>
  );
};

export const SeeAllButton = ({ onClick }: { onClick?: () => void }) => {
  return (
    <SeeAllContainer onClick={onClick}>
      <Button label="See All" variant="tertiary" size="XS" />
    </SeeAllContainer>
  );
};

const AccessibleLinkSeeAll = styled(AccessibleLink, {
  display: 'flex',
  alignItems: 'flex-start',
  variants: {
    isTablet: {
      true: {
        alignItems: 'flex-end',
      },
    },
  },
});

const SeeAllContainer = styled('div', {
  display: 'flex',
  gap: 11,
  alignItems: 'center',
  base: 'M',
  fontWeight: 600,
});
